import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { formatCourseDuration } from "@/lib/courseDuration";
import { Badge } from "@/components/ui/badge";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

export default function DisplayCourse({ course }) {
    const t = useTranslations();

    // Function to determine course level based on categories or course data
    const getCourseLevel = () => {
        if (course.level) return course.level;
        if (course.categories && course.categories.length > 0) {
            // Simple logic to determine level based on categories
            const categoryName =
                course.categories[0]?.name?.toLowerCase() || "";
            if (
                categoryName.includes("débutant") ||
                categoryName.includes("beginner")
            )
                return t("course_beginner");
            if (
                categoryName.includes("intermédiaire") ||
                categoryName.includes("intermediate")
            )
                return t("course_intermediate");
            if (
                categoryName.includes("avancé") ||
                categoryName.includes("advanced")
            )
                return t("course_advanced");
        }
        return t("course_beginner"); // Default
    };

    // Function to get course duration
    const getCourseDuration = () => {
        const duration = formatCourseDuration(course, t);
        if (duration) return duration;
        // Fallback or estimation
        return t("course_duration_hours", { hours: 10 });
    };

    return (
        <Link href={`/course/${course.slug}`} className="h-full">
            <Card className="hover:bg-gray-50 dark:hover:bg-grayDark-800 transition-all duration-100 h-full flex flex-col">
                <CardHeader className="pb-3 flex-1">
                    <div className="flex justify-between items-start">
                        <Image
                            src={course.image}
                            alt={course.name}
                            width={64}
                            height={64}
                        />
                        <div className="flex gap-2 flex-wrap">
                            {course.isPopular && (
                                <Badge>{t("course_popular")}</Badge>
                            )}
                            {course.isSoon && <Badge>{t("course_soon")}</Badge>}
                            {course.isNew && (
                                <Badge variant="confidence">
                                    {t("course_new")}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <CardTitle className="pt-4 leading-relaxed">
                        <h2>{course.name}</h2>
                    </CardTitle>
                    <CardDescription className="line-clamp-4">
                        {course.summary}
                    </CardDescription>
                </CardHeader>
                <CardContent className="px-3">
                    <div className="flex items-center gap-1">
                        <Badge variant="secondary">{getCourseLevel()}</Badge>
                        <span className="text-sm text-muted-foreground">•</span>
                        <Badge variant="secondary">{getCourseDuration()}</Badge>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
